(ns frontend.page-components.navbar-new-version.feedback-dropdown
  (:require
   [basic-components.attachments :as attachments-view]
   [basic-components.field-editor :as field-editor]
   [common.feedback-ratings :as feedback-ratings]
   [common.feedback-reasons :as feedback-reasons]
   [common.localization :as loc]
   [common.util :as cu]
   [frontend.logging :as logging]
   [frontend.page-components.events :as events]
   [frontend.state.current-session :as session]
   [frontend.utils :as fu]
   [frontend.view.components.upload :as upload]
   [potpuri.core :refer [fn-> fn->>]]
   [reagent.core :as r]
   [ui.components.button.view :as button]
   [ui.components.radix-ui.popover.view :as popover]
   [ui.components.radix-ui.tooltip.view :as tooltip]
   [ui.editors.data-input :as data-input]
   [ui.editors.labelled-input.view :as labelled-input]))

(defn feedback-dropdown-tools
  [dropdown-state feedback-state]
  [:div
   {:class-name "flex gap-1"}
   [button/square-secondary-open
    {:on-click #(swap! dropdown-state update :feedback-expanded? not)}
    [:i.fas.fa-compress-alt.text-xl]]
   [button/square-secondary-open
    {:on-click (fn []
                 (events/cancel-feedback! (:feedback-id @feedback-state) feedback-state)
                 (swap! dropdown-state assoc :dropdown-status/feedback false)
                 (reset! feedback-state {}))}
    [:i.fas.fa-times-circle.text-xl]]])

(defn feedback-reason-select
  [feedback-state]
  [:div.mt-3
   [data-input/view
    {:data-type   :data.type/keyword
     :value       (:feedback/reason @feedback-state)
     :placeholder (loc/tr :feedback-reason-type-placeholder)
     :options     (->> feedback-reasons/options
                       (mapv (fn [{:keys [key label]}]
                               {:value key
                                :label label})))
     :on-change   (fn [new-value & _]
                    (swap! feedback-state assoc :feedback/reason new-value))}]])

(defn feedback-description
  [feedback-state]
  [:div.mt-3
   [labelled-input/view
    {:label     (loc/tr :feedback-description-placeholder)
     :data-type :data.type/text-multiline
     :value     (:feedback/description @feedback-state)
     :on-change (fn [new-value]
                  (swap! feedback-state assoc :feedback/description new-value))}]])

;;combined max 50 MB:
(def MAX-ATTACHMENT-SIZE (* 50 1024 1024))

(defn feedback-attachments
  [feedback-state]
  (let [feedback-id (:feedback-id @feedback-state)
        feedback (get @session/entities feedback-id)
        attachments (->> feedback
                         :common/attachments
                         (keep (fn->> (get @session/entities)))
                         (remove :common/disabled?)
                         (sort-by :common/created-on)
                         reverse)
        common-attachments (set attachments)
        current-attachments-size (->> common-attachments
                                      (map :attachment/content-length)
                                      (filter number?)
                                      (apply +))
        pending-attachments-size (->> @feedback-state
                                      :uploading
                                      (map :size)
                                      (filter number?)
                                      (apply +))
        total-attachments-size (+ current-attachments-size
                                  pending-attachments-size)
        on-success! (fn [body]
                      (let [old-attachments (->> (get @session/entities feedback-id)
                                                 :common/attachments)
                            new-attachments (if (seq old-attachments)
                                              (conj old-attachments (:db/id body))
                                              #{(:db/id body)})]
                        (swap! feedback-state assoc :common/attachments new-attachments)))]
    [:div.attachment-selectors__upload-zone.mt-3
     [:div.d-inline-block.input-editor__label.pb-2
      (loc/tr :feedback-attachments-label)
      [tooltip/view
       {:side :bottom}
       [:i
        {:class-name (fu/cs :fas
                            :fa-info-circle
                            :ml-2)}]
       (loc/tr :feedback-attachments-tooltip)]]
     [upload/custom-upload-zone
      {:class-name "feedback-upload-zone"
       :on-change! (fn [{:keys [files original-event] :as args}]
                     (let [size (->> (aget files 0)
                                     (.-size))
                           new-size (+ (or size 0)
                                       total-attachments-size)]
                       (if (> new-size MAX-ATTACHMENT-SIZE)
                         (swap! feedback-state assoc
                                :error
                                (loc/tr :max-combined-attachment-size-error))
                         (events/add-attachment-to-feedback! {:db/id       feedback-id
                                                              :state       feedback-state
                                                              :on-success! on-success!
                                                              :on-error!   identity}
                                                             args))
                       (set! (-> original-event
                                 .-target
                                 .-value)
                             "")))
       :accept     ".jpg, .jpeg, .png, .pdf"}]
     [attachments-view/attachments-error feedback-state]
     [:div.attachment-listing.mb-3
      (doall
       (for [indicator (:uploading @feedback-state)]
         ^{:key (str "uploading-attachment-" (:token indicator))}
         [attachments-view/indicator-view feedback-state indicator]))
      (doall
       (for [attachment attachments]
         ^{:key (str "attachment-" (:db/id attachment))}
         [attachments-view/attachment-view-with-remove attachment feedback-id]))]]))

(defn feedback-rating-emoji
  [option-key checked?]
  (let [emoji (case option-key
                :feedback-rating/very-dissatisfied :fa-sad-tear
                :feedback-rating/dissatisfied :fa-frown
                :feedback-rating/neutral :fa-meh
                :feedback-rating/satisfied :fa-grin
                :feedback-rating/very-satisfied :fa-grin-hearts)]
    [:i
     {:class-name (fu/cs (if checked? "fas" "fal")
                         emoji)}]))

(defn feedback-rating
  [feedback-state]
  [:div.mt-3
   [:div.d-inline-block.input-editor__label
    (loc/tr :feedback-rating-label)
    [:i.fas.fa-asterisk.input-editor__label-mandatory]
    [:div.mt-2.d-flex.justify-space-between
     (doall
      (for [option feedback-ratings/options
            :let [option-value (:value option)
                  option-key (:key option)
                  checked? (= (:feedback/rating @feedback-state) option-value)]]
        ^{:key (str "feedback-rating-" option-key)}
        [:div.feedback-rating__item
         [:label.feedback-rating__label
          [:input
           {:type       :radio
            :class-name (fu/cs "feedback-rating__input")
            :on-change  (fn [e]
                          (swap! feedback-state assoc
                                 :feedback/rating (double option-value)
                                 :feedback/rating-key option-key)
                          (deref feedback-state))
            :checked    checked?}]
          [:span.feedback-rating__icon [feedback-rating-emoji option-key checked?]]]]))]]])

(defn feedback-follow-up
  [feedback-state]
  [:div.mb-4
   [field-editor/view
    {:editor/type  :editor.type/checkbox
     :value        (:feedback/follow-up? @feedback-state)
     :state-atom   feedback-state
     :attribute    :feedback/follow-up?
     :label        (loc/tr :feedback-follow-up-label)
     :on-save!     (fn [e new-value clear-cb!]
                     (swap! feedback-state assoc :feedback/follow-up? new-value)
                     (deref feedback-state))
     :drag-options {:hide-drag-area? true}}]])

(defn submit-feedback-button
  [feedback-state dropdown-state some-required-fields-empty?]
  (let [on-success! (fn []
                      (logging/info "Successfully send feedback")
                      (swap! dropdown-state assoc :feedback-confirmation-view true)
                      (swap! dropdown-state dissoc :feedback-expanded? :processing?)
                      (reset! feedback-state {}))
        on-error! (fn [e]
                    (logging/error "error with feedback " e)
                    (swap! feedback-state assoc :feedback-error (loc/tr :feedback-error)))
        data (select-keys @feedback-state
                          [:feedback-id :feedback/rating :feedback/reason :feedback/rating-key
                           :feedback/description :feedback/follow-up?
                           :common/attachments])
        disabled? (or some-required-fields-empty?
                      (not-empty (:uploading @feedback-state)))]
    [button/primary-solid
     {:disabled? disabled?
      :tooltip   (when disabled?
                   (loc/tr :please-fill-in-all-details))
      :on-click  (fn []
                   (when-not (or some-required-fields-empty?
                                 (not-empty (:uploading @feedback-state)))
                     (swap! dropdown-state assoc :processing? true)
                     (events/send-feedback! data on-success! on-error!)))}
     (if (:processing? @dropdown-state)
       [:span.feedback-dropdown__spinner-wrapper
        [:img.feedback-dropdown__spinner
         {:src "/img/spinner.svg"}]
        (loc/tr :submitting-feedback)]
       (loc/tr :submit))]))

(defn feedback-title
  []
  [:div
   [:div
    {:class "text-base-light-text text-xl"}
    (loc/tr :feedback-title)]])

(defn feedback-confirmation-view
  [dropdown-state feedback-state]
  [:div.p-3.text-center
   [:img
    {:class-name "img-fluid"
     :src        "/img/feedback-confirmation.svg"}]
   [:h2.title-h2.my-3 (loc/tr :feedback-confirmation-title)]
   [:div.mb-3 (loc/tr :feedback-confirmation-info)]
   [:div.d-flex.flex-column.justify-content-center
    [button/primary-solid
     {:on-click (fn []
                  (swap! dropdown-state dissoc
                         :dropdown-status/feedback
                         :feedback-confirmation-view))}
     (loc/tr :feedback-close)]
    [button/primary-open
     {:on-click (fn []
                  (swap! dropdown-state dissoc :feedback-confirmation-view)
                  (swap! dropdown-state assoc :feedback-expanded? true)
                  (when-not (:feedback-id @feedback-state)
                    (events/create-feedback! feedback-state)))}
     (loc/tr :feedback-add)]]])

(defn expanded-view
  [{:keys [dropdown-state feedback-state some-required-fields-empty?]}]
  [:<>
   [:div.flex.items-center.justify-between
    [feedback-title]
    [feedback-dropdown-tools dropdown-state feedback-state]]
   [feedback-rating feedback-state]
   [:div.flex.items-center.mt-3
    [:div.title-h4 (loc/tr :additional-feedback)
     [:i.fas.fa-asterisk.input-editor__label-mandatory]]]
   [:div.md:grid.grid-cols-10.gap-4
    [:div
     {:class "col-span-10"}
     [feedback-reason-select feedback-state]]]
   [feedback-description feedback-state]
   [feedback-attachments feedback-state]
   [feedback-follow-up feedback-state]
   (when (:feedback-error @feedback-state)
     [:div
      {:class-name "infobox infobox--danger mb-3"}
      [:span.infobox__remove.pointer
       {:on-click #(swap! feedback-state dissoc :feedback-error)}
       [:i.fal.fa-times]]
      [:div.flex.items-center
       [:span.infobox__icon
        [:i.fas.fa-exclamation-circle]]
       [:div.infobox__message (:feedback-error @feedback-state)]]])
   [submit-feedback-button feedback-state dropdown-state some-required-fields-empty?]])

(defn collapsed-view
  [dropdown-state feedback-state]
  [:div.flex.justify-between.items-center
   [feedback-title]
   [feedback-dropdown-tools dropdown-state feedback-state]])

(defn dropdown
  [dropdown-state]
  (let [feedback-state (r/atom {})]
    (swap! dropdown-state assoc :feedback-expanded? true)
    (fn [dropdown-state]
      (let [feedback-dropdown-open? (:dropdown-status/feedback @dropdown-state)
            feedback-expanded-view? (:feedback-expanded? @dropdown-state)
            required-fields [:feedback/reason
                             :feedback/description
                             :feedback/rating]
            some-required-fields-empty? (->> required-fields
                                             (some (fn [required-field]
                                                     (cu/blank-value? (get @feedback-state
                                                                           required-field)))))
            feedback-confirmation-view? (:feedback-confirmation-view @dropdown-state)]
        [:div
         [popover/view
          {:open?          feedback-dropdown-open?
           :on-open-change (fn [_]
                             (js/console.log "Chnage : " feedback-dropdown-open?)
                             (let [open? (not feedback-dropdown-open?)]
                               (swap! dropdown-state assoc
                                      :dropdown-status/feedback (not feedback-dropdown-open?)
                                      :feedback-expanded? true)
                               (js/console.log "Fedback popover : " open? " " (empty @feedback-state) " << " @feedback-state)
                               (when (and open?
                                          (empty? @feedback-state))
                                 (events/create-feedback! feedback-state))))}
          ;[tooltip/view
          [button/secondary-open
           {:tooltip  (loc/tr :feedback-tooltip)
            :dom-id "navbar-feedback-button"
            :on-click (fn [e]
                        (js/console.log "Fedback clicked "))}
           [:i.fas.fa-thumbs-up]]
          ;tooltip]
          [:div.feedback-dropdown__content.py-4.px-3
           {:class "w-92 px-3 py-4 text-base-light-text"}
           (cond
             feedback-expanded-view?
             [expanded-view
              {:dropdown-state              dropdown-state
               :feedback-state              feedback-state
               :some-required-fields-empty? some-required-fields-empty?}]
             (and feedback-confirmation-view?
                  (not feedback-expanded-view?))
             [feedback-confirmation-view dropdown-state feedback-state]
             :else
             [collapsed-view dropdown-state feedback-state])]]]))))
